import React, { useState, useEffect } from 'react';
import { adminApi, landlordApi } from '../../services/api';
import Navbar from '../Dashboard/components/Navbar';

interface Partner {
    id: string;
    businessName: string;
    contactEmail: string;
    contactName?: string;
    tradeType: string;
    verificationStatus: 'pending' | 'verified' | 'rejected';
    phone?: string;
    address?: string;
    yearsExperience?: number;
    jobsCompleted?: number;
    averageRating?: number;
    createdAt?: string;
    yellowCards?: number;
    greenCards?: number;
    cardHistory?: { type: 'yellow' | 'green'; reason: string; issuedAt: string; issuedBy: string }[];
}

export default function PartnersAdmin() {
    const [partners, setPartners] = useState<Partner[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'verified' | 'rejected'>('all');
    const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Card modal state
    const [cardModal, setCardModal] = useState<{ partner: Partner; type: 'yellow' | 'green' } | null>(null);
    const [cardReason, setCardReason] = useState('');

    useEffect(() => {
        fetchPartners();
    }, []);

    const fetchPartners = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
            const headers = { Authorization: `Bearer ${token}` };

            const res = await adminApi.get('api/super-admin/partners', { headers });
            const raw = res.data?.partners || res.data?.data || [];
            // Ensure card counts default to 0
            const withCards = raw.map((p: any) => ({
                ...p,
                yellowCards: p.yellowCards || 0,
                greenCards: p.greenCards || 0,
                cardHistory: p.cardHistory || []
            }));
            setPartners(withCards);
        } catch (err: any) {
            console.error('Failed to fetch partners:', err);
            setError(err.response?.data?.error?.message || 'Failed to load partners');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyPartner = async (partnerId: string) => {
        try {
            setActionLoading(true);
            const token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
            const headers = { Authorization: `Bearer ${token}` };

            await landlordApi.post(`api/partners/${partnerId}/verify`, {}, { headers });

            setPartners(partners.map(p =>
                p.id === partnerId ? { ...p, verificationStatus: 'verified' } : p
            ));
            setSelectedPartner(null);
        } catch (err: any) {
            console.error('Failed to verify partner:', err);
            alert('Failed to verify partner: ' + (err.response?.data?.error?.message || err.message));
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectPartner = async (partnerId: string) => {
        try {
            setActionLoading(true);
            const token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
            const headers = { Authorization: `Bearer ${token}` };

            await landlordApi.post(`api/partners/${partnerId}/reject`, {}, { headers });

            setPartners(partners.map(p =>
                p.id === partnerId ? { ...p, verificationStatus: 'rejected' } : p
            ));
            setSelectedPartner(null);
        } catch (err: any) {
            console.error('Failed to reject partner:', err);
            alert('Failed to reject partner: ' + (err.response?.data?.error?.message || err.message));
        } finally {
            setActionLoading(false);
        }
    };

    const handleIssueCard = async () => {
        if (!cardModal || !cardReason.trim()) {
            alert('Please provide a reason for the card.');
            return;
        }

        const { partner, type } = cardModal;

        try {
            setActionLoading(true);
            const token = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
            const headers = { Authorization: `Bearer ${token}` };

            // Issue card via backend (strike endpoint for yellow, commend for green)
            if (type === 'yellow') {
                await landlordApi.post(`api/super-admin/partners/${partner.id}/strike`, { reason: cardReason }, { headers });
            } else {
                // Green card — use general update or a new endpoint. For now, update locally and persist via partner update.
                await landlordApi.post(`api/super-admin/partners/${partner.id}/strike`, {
                    reason: `🟢 GREEN CARD (Commendation): ${cardReason}`,
                    cardType: 'green'
                }, { headers }).catch(() => {
                    // If strike endpoint doesn't support green cards, just log for now
                    console.warn('[PartnersAdmin] Green card issued locally — backend integration pending');
                });
            }

            // Update local state
            const newEntry = {
                type,
                reason: cardReason,
                issuedAt: new Date().toISOString(),
                issuedBy: 'admin@compliance-engine.io'
            };

            setPartners(partners.map(p => {
                if (p.id !== partner.id) return p;
                const updatedYellow = type === 'yellow' ? (p.yellowCards || 0) + 1 : p.yellowCards || 0;
                const updatedGreen = type === 'green' ? (p.greenCards || 0) + 1 : p.greenCards || 0;
                return {
                    ...p,
                    yellowCards: updatedYellow,
                    greenCards: updatedGreen,
                    cardHistory: [...(p.cardHistory || []), newEntry]
                };
            }));

            setCardModal(null);
            setCardReason('');
            alert(`${type === 'green' ? '🟢 Green' : '🟡 Yellow'} card issued to ${partner.businessName}`);
        } catch (err: any) {
            console.error(`Failed to issue ${type} card:`, err);
            alert(`Failed to issue card: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    /** Net card score: green (+1) - yellow (-1). Positive = good standing. */
    const getNetScore = (partner: Partner) => {
        return (partner.greenCards || 0) - (partner.yellowCards || 0);
    };

    const getScoreBadge = (partner: Partner) => {
        const net = getNetScore(partner);
        if (net > 0) return { text: `+${net}`, bgClass: 'bg-green-100 text-green-800', icon: '🟢' };
        if (net < 0) return { text: `${net}`, bgClass: 'bg-red-100 text-red-800', icon: '🔴' };
        return { text: '0', bgClass: 'bg-gray-100 text-gray-600', icon: '⚪' };
    };

    const filteredPartners = filterStatus === 'all'
        ? partners
        : partners.filter(p => p.verificationStatus === filterStatus);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
                    <p className="text-gray-600">Loading Partners Dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Navbar />
            <div className="max-w-7xl mx-auto p-8 w-full flex-1">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-900">Partners Admin Dashboard</h1>
                    <p className="text-gray-600 mt-2">Manage engineering partners, verification, cards, and assignments</p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800">{error}</p>
                    </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm text-gray-600 mb-1">Total Partners</div>
                        <div className="text-3xl font-bold text-gray-900">{partners.length}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm text-gray-600 mb-1">Verified</div>
                        <div className="text-3xl font-bold text-green-600">
                            {partners.filter(p => p.verificationStatus === 'verified').length}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm text-gray-600 mb-1">Pending Verification</div>
                        <div className="text-3xl font-bold text-yellow-600">
                            {partners.filter(p => p.verificationStatus === 'pending').length}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
                        <div className="text-sm text-gray-600 mb-1">🟢 Green Cards Issued</div>
                        <div className="text-3xl font-bold text-green-600">
                            {partners.reduce((sum, p) => sum + (p.greenCards || 0), 0)}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Commendations (+1)</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
                        <div className="text-sm text-gray-600 mb-1">🟡 Yellow Cards Issued</div>
                        <div className="text-3xl font-bold text-yellow-600">
                            {partners.reduce((sum, p) => sum + (p.yellowCards || 0), 0)}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Warnings (-1)</p>
                    </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex gap-4 mb-6 border-b border-gray-200">
                    {(['all', 'pending', 'verified', 'rejected'] as const).map(status => (
                        <button
                            key={status}
                            onClick={() => setFilterStatus(status)}
                            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                                filterStatus === status
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            {status === 'all' ? 'All Partners' : status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                    ))}
                </div>

                {/* Partners Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredPartners.length === 0 ? (
                        <div className="col-span-full text-center py-12">
                            <i className="fas fa-inbox text-4xl text-gray-300 mb-4"></i>
                            <p className="text-gray-500">No partners found with this status</p>
                        </div>
                    ) : (
                        filteredPartners.map(partner => {
                            const scoreBadge = getScoreBadge(partner);
                            return (
                                <div key={partner.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                                    <div className="p-6">
                                        {/* Status Badge + Net Score */}
                                        <div className="flex justify-between items-start mb-3">
                                            <h3 className="text-lg font-bold text-gray-900">{partner.businessName}</h3>
                                            <div className="flex items-center gap-2">
                                                {/* Net card score badge */}
                                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${scoreBadge.bgClass}`} title={`Net Score: Green(${partner.greenCards || 0}) - Yellow(${partner.yellowCards || 0})`}>
                                                    {scoreBadge.icon} {scoreBadge.text}
                                                </span>
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                                                    partner.verificationStatus === 'verified' ? 'bg-green-100 text-green-800' :
                                                    partner.verificationStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-red-100 text-red-800'
                                                }`}>
                                                    {partner.verificationStatus}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Partner Details */}
                                        <div className="space-y-2 mb-4 text-sm text-gray-600">
                                            <p><strong>Contact:</strong> {partner.contactName || partner.contactEmail}</p>
                                            <p><strong>Email:</strong> {partner.contactEmail}</p>
                                            <p><strong>Type:</strong> {partner.tradeType}</p>
                                            {partner.phone && <p><strong>Phone:</strong> {partner.phone}</p>}
                                            {partner.yearsExperience && <p><strong>Experience:</strong> {partner.yearsExperience} years</p>}
                                            {partner.jobsCompleted !== undefined && <p><strong>Jobs:</strong> {partner.jobsCompleted}</p>}
                                            {partner.averageRating && <p><strong>Rating:</strong> ⭐ {partner.averageRating}/5</p>}
                                        </div>

                                        {/* Card History Summary */}
                                        {((partner.greenCards || 0) > 0 || (partner.yellowCards || 0) > 0) && (
                                            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs">
                                                <div className="flex justify-between items-center">
                                                    <span className="font-semibold text-gray-700">Card Record</span>
                                                    <div className="flex gap-2">
                                                        <span className="text-green-700 font-bold">🟢 {partner.greenCards || 0}</span>
                                                        <span className="text-yellow-700 font-bold">🟡 {partner.yellowCards || 0}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Card Action Buttons — Always visible for verified partners */}
                                        {partner.verificationStatus === 'verified' && (
                                            <div className="flex gap-2 mb-3">
                                                <button
                                                    onClick={() => { setCardModal({ partner, type: 'green' }); setCardReason(''); }}
                                                    className="flex-1 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg font-medium text-sm hover:bg-green-100 transition-colors flex items-center justify-center gap-1"
                                                >
                                                    <span>🟢</span> Green Card
                                                </button>
                                                <button
                                                    onClick={() => { setCardModal({ partner, type: 'yellow' }); setCardReason(''); }}
                                                    className="flex-1 px-3 py-2 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-lg font-medium text-sm hover:bg-yellow-100 transition-colors flex items-center justify-center gap-1"
                                                >
                                                    <span>🟡</span> Yellow Card
                                                </button>
                                            </div>
                                        )}

                                        {/* Action Buttons - Verify/Reject for pending */}
                                        {partner.verificationStatus === 'pending' && (
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleVerifyPartner(partner.id)}
                                                    disabled={actionLoading}
                                                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700 disabled:opacity-50"
                                                >
                                                    {actionLoading ? 'Processing...' : 'Verify'}
                                                </button>
                                                <button
                                                    onClick={() => handleRejectPartner(partner.id)}
                                                    disabled={actionLoading}
                                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded font-medium hover:bg-red-700 disabled:opacity-50"
                                                >
                                                    {actionLoading ? 'Processing...' : 'Reject'}
                                                </button>
                                            </div>
                                        )}

                                        {/* Joined Date */}
                                        {partner.createdAt && (
                                            <p className="text-xs text-gray-400 mt-4">
                                                Joined: {new Date(partner.createdAt).toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Card Issue Modal */}
            {cardModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setCardModal(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className={`px-6 py-4 ${cardModal.type === 'green' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-yellow-500 to-amber-600'}`}>
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold text-white">
                                    {cardModal.type === 'green' ? '🟢 Issue Green Card (+1)' : '🟡 Issue Yellow Card (-1)'}
                                </h3>
                                <button onClick={() => setCardModal(null)} className="text-white/80 hover:text-white text-xl">&times;</button>
                            </div>
                            <p className="text-white/80 text-sm mt-1">
                                {cardModal.type === 'green'
                                    ? 'Commend this partner for outstanding work. A green card neutralises a yellow card.'
                                    : 'Issue a warning for substandard performance. Partner will be notified.'
                                }
                            </p>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6">
                            <div className="mb-4">
                                <p className="text-sm text-gray-600 mb-1">Partner:</p>
                                <p className="font-bold text-gray-900">{cardModal.partner.businessName}</p>
                                <p className="text-xs text-gray-500">{cardModal.partner.contactEmail}</p>
                            </div>

                            <div className="mb-4 p-3 bg-gray-50 rounded-lg text-xs">
                                <div className="flex justify-between">
                                    <span>Current Green Cards:</span>
                                    <span className="font-bold text-green-600">{cardModal.partner.greenCards || 0}</span>
                                </div>
                                <div className="flex justify-between mt-1">
                                    <span>Current Yellow Cards:</span>
                                    <span className="font-bold text-yellow-600">{cardModal.partner.yellowCards || 0}</span>
                                </div>
                                <div className="flex justify-between mt-1 pt-1 border-t border-gray-200">
                                    <span className="font-semibold">Net Score:</span>
                                    <span className={`font-bold ${getNetScore(cardModal.partner) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {getNetScore(cardModal.partner) >= 0 ? '+' : ''}{getNetScore(cardModal.partner)}
                                        {cardModal.type === 'green' ? ' → ' + (getNetScore(cardModal.partner) + 1) : ' → ' + (getNetScore(cardModal.partner) - 1)}
                                    </span>
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reason <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={cardReason}
                                    onChange={(e) => setCardReason(e.target.value)}
                                    placeholder={cardModal.type === 'green'
                                        ? 'E.g., Exceptional quality on gas safety inspection at 12 Baker St. Tenant feedback: 5/5'
                                        : 'E.g., Late attendance at scheduled EPC appointment. Tenant complained about no-show.'
                                    }
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setCardModal(null)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleIssueCard}
                                    disabled={actionLoading || !cardReason.trim()}
                                    className={`flex-1 px-4 py-2 text-white rounded-lg font-medium disabled:opacity-50 ${
                                        cardModal.type === 'green'
                                            ? 'bg-green-600 hover:bg-green-700'
                                            : 'bg-yellow-600 hover:bg-yellow-700'
                                    }`}
                                >
                                    {actionLoading ? 'Issuing...' : `Issue ${cardModal.type === 'green' ? 'Green' : 'Yellow'} Card`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
